/**
 * SmoothnessDashboardOverlayContent (LRG.29) — in-game mirror of the
 * dj_h2000_smoothness_dashboard MCP probe. ONE panel that pulls all
 * three pillars (server / netcode / frame pipeline) every 3s and shows
 * an overall verdict + per-pillar details.
 *
 * Companion to the MCP tool — operators can choose to see the same data
 * either via MCP (remote) or via this in-game tool (live during play).
 *
 * data-testid="smoothness-dashboard" + per-pillar data-severity attrs.
 */

import React, { useEffect, useState } from 'react';
import { CopyButton } from '../CopyButton';
import { deriveInterpVerdict } from '../../../diagnostics/interpVerdict';

const POLL_MS = 3000;

type Severity = 'healthy' | 'mild' | 'severe' | 'unknown';

interface PillarState {
  severity: Severity;
  summary: string;
  detail: string;
}

interface DashboardState {
  overall: Severity;
  overallSummary: string;
  server: PillarState;
  netcode: PillarState;
  framePipeline: PillarState;
  deployedSha: string | null;
  lastPolledAt: number | null;
  lastError: string | null;
}

const SEVERITY_COLOR: Record<Severity, string> = {
  healthy: '#42d77d',
  mild: '#fa5',
  severe: '#ff5f5f',
  unknown: '#888',
};

const SEVERITY_ICON: Record<Severity, string> = {
  healthy: '🟢',
  mild: '🟡',
  severe: '🔴',
  unknown: '⚪',
};

const SEVERITY_RANK: Record<Severity, number> = { healthy: 0, unknown: 1, mild: 1, severe: 2 };

const EMPTY: DashboardState = {
  overall: 'unknown',
  overallSummary: 'Loading…',
  server: { severity: 'unknown', summary: '—', detail: '' },
  netcode: { severity: 'unknown', summary: '—', detail: '' },
  framePipeline: { severity: 'unknown', summary: '—', detail: '' },
  deployedSha: null,
  lastPolledAt: null,
  lastError: null,
};

async function fetchJson(path: string): Promise<any | null> {
  try {
    const r = await fetch(path, { credentials: 'omit' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

async function poll(): Promise<DashboardState> {
  const [version, health, diagHealth, jitter, snapStats] = await Promise.all([
    fetchJson('/api/version'),
    fetchJson('/api/health'),
    fetchJson('/api/diag/health'),
    fetchJson('/api/diag/frame-jitter'),
    fetchJson('/api/diag/snapshot-stats'),
  ]);

  // Server
  const apiHealthy = !!health;
  const diagHooked = !!diagHealth?.hooked;
  const serverSeverity: Severity = apiHealthy && diagHooked ? 'healthy' : 'severe';
  const server: PillarState = {
    severity: serverSeverity,
    summary: apiHealthy ? 'API + diag hooks healthy' : 'API unreachable',
    detail: `sha ${(version?.sha ?? 'unknown').slice(0, 8)} · env ${version?.env ?? '?'}`,
  };

  // Netcode — inter-client interpolation health via the shared
  // deriveInterpVerdict() (single source of truth for the thresholds; also
  // drives the /diagnostic SmoothnessTab).
  const interpVerdict = deriveInterpVerdict(snapStats);
  const netcode: PillarState = {
    severity: interpVerdict.severity,
    summary: interpVerdict.summary,
    detail: interpVerdict.detail,
  };

  // Frame pipeline.
  //
  // DEBUG-SMOOTH-JITTER-UNKNOWN-01 (2026-07-27): the server's
  // `synthesizeFrameJitterVerdict` returns `severity: 'healthy'` when
  // `activeSessions === 0` (diag.routes.ts:454), so this pillar used to paint
  // 🟢 healthy over the words "No active sessions reporting frame jitter
  // data." — a green health badge for an admitted absence of data, which the
  // 2026-07-22 devkit audit banked as part of a "working" verdict. Its own
  // sibling gets this right: `deriveInterpVerdict` returns `unknown` on no
  // samples, which is why NETCODE read ⚪ in the same capture. Match it.
  //
  // Fixed client-side first, deliberately: `/api/diag/frame-jitter`'s verdict
  // is also read by the MCP probe and the perf gate, so flipping the server
  // string was staged separately (see the audit's corrected-methodology
  // section). The pillar owns its own presentation.
  //
  // The server side landed later the same day: the endpoint now returns
  // `severity: 'unknown'` at zero sessions (diag.routes.ts, `DiagSeverity`),
  // and `dj_h2000_smoothness_dashboard` was taught `unknown` BEFORE that
  // shipped. This guard stays anyway — it is what keeps the pillar honest
  // against an older deploy that still says 'healthy'.
  const jitterVerdict = jitter?.verdict ?? {};
  const jitterHasSessions = (jitter?.totalActiveSessions ?? 0) > 0;
  const framePipeline: PillarState = {
    severity: jitterHasSessions ? ((jitterVerdict.severity ?? 'unknown') as Severity) : 'unknown',
    summary: jitterHasSessions
      ? (jitterVerdict.summary || 'No data')
      : 'no sessions reporting frame jitter — not a healthy result, an absent one',
    detail: jitterHasSessions
      ? `p95=${(jitter.worstP95 ?? 0).toFixed(1)}ms · max=${(jitter.worstMax ?? 0).toFixed(1)}ms · ${jitter.totalSpikesLast10s ?? 0} spikes/10s`
      : 'enable the client frame-jitter tracker (?framejitter=1) to feed this pillar',
  };

  const overallRank = Math.max(
    SEVERITY_RANK[server.severity],
    SEVERITY_RANK[netcode.severity],
    SEVERITY_RANK[framePipeline.severity],
  );
  const overall: Severity = overallRank === 0 ? 'healthy' : overallRank === 1 ? 'mild' : 'severe';
  const overallSummary = overall === 'healthy'
    ? 'All systems healthy — netcode + rendering + server in sync.'
    : `${overall.toUpperCase()} — see details below.`;

  return {
    overall,
    overallSummary,
    server,
    netcode,
    framePipeline,
    deployedSha: (version?.sha ?? null),
    lastPolledAt: Date.now(),
    lastError: null,
  };
}

export const SmoothnessDashboardOverlayContent: React.FC = () => {
  const [state, setState] = useState<DashboardState>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const next = await poll();
      if (!cancelled) setState(next);
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const overallColor = SEVERITY_COLOR[state.overall];

  const copyText = () => [
    `SMOOTHNESS DASHBOARD @ ${new Date().toISOString()}`,
    `overall:        ${SEVERITY_ICON[state.overall]} ${state.overall}`,
    `summary:        ${state.overallSummary}`,
    `deployedSha:    ${state.deployedSha ?? '?'}`,
    '',
    `server:         ${SEVERITY_ICON[state.server.severity]} ${state.server.severity}`,
    `  summary:      ${state.server.summary}`,
    `  detail:       ${state.server.detail}`,
    `netcode:        ${SEVERITY_ICON[state.netcode.severity]} ${state.netcode.severity}`,
    `  summary:      ${state.netcode.summary}`,
    `  detail:       ${state.netcode.detail}`,
    `framePipeline:  ${SEVERITY_ICON[state.framePipeline.severity]} ${state.framePipeline.severity}`,
    `  summary:      ${state.framePipeline.summary}`,
    `  detail:       ${state.framePipeline.detail}`,
    `lastPolled:     ${state.lastPolledAt ? new Date(state.lastPolledAt).toLocaleTimeString('en-US', { hour12: false }) : '—'}`,
  ].join('\n');

  const pillarStyle = (sev: Severity): React.CSSProperties => ({
    padding: '6px 8px',
    borderRadius: 4,
    border: `1px solid ${SEVERITY_COLOR[sev]}`,
    background: `${SEVERITY_COLOR[sev]}11`,
    color: SEVERITY_COLOR[sev],
    marginBottom: 4,
    fontSize: 10,
  });

  return (
    <div
      data-testid="smoothness-dashboard"
      data-overall={state.overall}
      data-server={state.server.severity}
      data-netcode={state.netcode.severity}
      data-frame-pipeline={state.framePipeline.severity}
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 11,
        padding: '6px 8px',
        userSelect: 'none',
        lineHeight: 1.35,
      }}
    >
      <div style={{ color: '#7af', marginBottom: 4, letterSpacing: '0.05em', fontSize: 10, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
        <span>SMOOTHNESS DASHBOARD · polled {POLL_MS}ms</span>
        <CopyButton getText={copyText} />
      </div>

      <div style={{
        padding: '8px 10px',
        borderRadius: 5,
        border: `2px solid ${overallColor}`,
        background: `${overallColor}15`,
        color: overallColor,
        fontSize: 12,
        fontWeight: 700,
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <span style={{ fontSize: 18 }}>{SEVERITY_ICON[state.overall]}</span>
        <span>{state.overallSummary}</span>
      </div>

      <div style={pillarStyle(state.server.severity)}>
        <div style={{ fontWeight: 700 }}>{SEVERITY_ICON[state.server.severity]} SERVER · {state.server.severity}</div>
        <div style={{ color: '#aaa', fontSize: 9 }}>{state.server.summary}</div>
        <div style={{ color: '#666', fontSize: 9 }}>{state.server.detail}</div>
      </div>

      <div style={pillarStyle(state.netcode.severity)}>
        <div style={{ fontWeight: 700 }}>{SEVERITY_ICON[state.netcode.severity]} NETCODE · {state.netcode.severity}</div>
        <div style={{ color: '#aaa', fontSize: 9 }}>{state.netcode.summary}</div>
        <div style={{ color: '#666', fontSize: 9 }}>{state.netcode.detail}</div>
      </div>

      <div style={pillarStyle(state.framePipeline.severity)}>
        <div style={{ fontWeight: 700 }}>{SEVERITY_ICON[state.framePipeline.severity]} FRAME PIPELINE · {state.framePipeline.severity}</div>
        <div style={{ color: '#aaa', fontSize: 9 }}>{state.framePipeline.summary}</div>
        <div style={{ color: '#666', fontSize: 9 }}>{state.framePipeline.detail}</div>
      </div>

      <div style={{ fontSize: 9, color: '#666', marginTop: 4 }}>
        sha {(state.deployedSha ?? '?').slice(0, 8)} ·
        last poll {state.lastPolledAt ? new Date(state.lastPolledAt).toLocaleTimeString('en-US', { hour12: false }) : '—'}
      </div>
    </div>
  );
};
